import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import "./App.css";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";

import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ResetPassword from "./pages/ResetPassword";

import CrmLayout from "./components/crm/CrmLayout";
import Dashboard from "./pages/crm/Dashboard";
import Customers from "./pages/crm/Customers";
import Materials from "./pages/crm/Materials";
import Inventory from "./pages/crm/Inventory";
import Leads from "./pages/crm/Leads";
import Opportunities from "./pages/crm/Opportunities";
import OpportunityDetail from "./pages/crm/OpportunityDetail";
import ServiceTickets from "./pages/crm/ServiceTickets";
import Quotations from "./pages/crm/Quotations";
import InstalledBase from "./pages/crm/InstalledBase";
import { Vendors, SalesEmployees, CustomerGroups, MaterialGroups } from "./pages/crm/SimpleMasters";
import AdminTechnicians from "./pages/crm/AdminTechnicians";

import CustomerAppLayout from "./components/customer-app/CustomerAppLayout";
import CustomerLogin from "./pages/customer-app/CustomerLogin";
import CustomerOnboarding from "./pages/customer-app/CustomerOnboarding";
import CustomerHome from "./pages/customer-app/CustomerHome";
import CustomerInstallations from "./pages/customer-app/CustomerInstallations";
import CustomerTickets from "./pages/customer-app/CustomerTickets";
import CustomerRaiseComplaint from "./pages/customer-app/CustomerRaiseComplaint";
import CustomerBuyInstallation from "./pages/customer-app/CustomerBuyInstallation";
import CustomerProfile from "./pages/customer-app/CustomerProfile";
import CustomerQuotation from "./pages/customer-app/CustomerQuotation";
import CustomerTrackProgress from "./pages/customer-app/CustomerTrackProgress";
import CustomerReferralProgress from "./pages/customer-app/CustomerReferralProgress";
import CustomerPastOrders from "./pages/customer-app/CustomerPastOrders";

import FsmLayout from "./components/mobile/FsmLayout";
import FsmTickets from "./pages/fsm/FsmTickets";
import FsmWarranty from "./pages/fsm/FsmWarranty";
import FsmLookup from "./pages/fsm/FsmLookup";

import PublicQuote from "./pages/PublicQuote";
import PublicSurvey from "./pages/PublicSurvey";
import PublicScLead from "./pages/PublicScLead";
import PublicTrack from "./pages/PublicTrack";
import ScSignup from "./pages/sc/ScSignup";
import ScLayout from "./components/sc/ScLayout";
import ScHome from "./pages/sc/ScHome";
import ScNewLead from "./pages/sc/ScNewLead";
import ScLeads from "./pages/sc/ScLeads";
import ScServiceRequests from "./pages/sc/ScServiceRequests";
import ScEarnings from "./pages/sc/ScEarnings";
import ScMarketing from "./pages/sc/ScMarketing";
import ScProfile from "./pages/sc/ScProfile";
import ScTarget from "./pages/sc/ScTarget";
import ScCalculator from "./pages/sc/ScCalculator";
import ScExplainers from "./pages/sc/ScExplainers";
import ScFaq from "./pages/sc/ScFaq";
import ScCampaigns from "./pages/sc/ScCampaigns";
import ScFlyer from "./pages/sc/ScFlyer";
import ScSchemes from "./pages/sc/ScSchemes";
import IncentiveSlabs from "./pages/crm/IncentiveSlabs";
import StateBenefits from "./pages/crm/StateBenefits";
import SalesTargets from "./pages/crm/SalesTargets";
import SolarConsultants from "./pages/crm/SolarConsultants";
import ReferrerPayouts from "./pages/crm/ReferrerPayouts";
import PermissibleTerritories from "./pages/crm/PermissibleTerritories";
import ServiceTeam from "./pages/crm/ServiceTeam";
import SLAMaster from "./pages/crm/SLAMaster";
import Campaigns from "./pages/crm/Campaigns";
import ServiceTeamLayout from "./components/service/ServiceTeamLayout";
import ServiceDashboard from "./pages/service/ServiceDashboard";
import ServiceTeamTickets from "./pages/service/ServiceTeamTickets";
import ServiceTeamTechnicians from "./pages/service/ServiceTeamTechnicians";
import { PUBLIC_PATHS } from "./lib/publicPaths";

// Map public path → component (driven by lib/publicPaths.js single source of truth)
const PUBLIC_ROUTE_COMPONENTS = {
  "/quote": <PublicQuote />,
  "/app/quote": <PublicQuote />,
  "/public/quote": <PublicQuote />,
  "/survey": <PublicSurvey />,
  "/app/survey": <PublicSurvey />,
  "/public/survey": <PublicSurvey />,
  "/track": <PublicTrack />,
  "/get-quote": <PublicScLead />,
};

function HomeRedirect() {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin" || user.role === "sales_employee") return <Navigate to="/crm" replace />;
  if (user.role === "technician") return <Navigate to="/fsm" replace />;
  if (user.role === "sc") return <Navigate to="/sc" replace />;
  if (user.role === "service_team") return <Navigate to="/service" replace />;
  return <Navigate to="/app" replace />;
}

// /t/:token short-link → redirects to /track?token=:token (keeps a single tracker page)
function TrackShortLink() {
  const { token } = useParams();
  return <Navigate to={`/track?token=${encodeURIComponent(token || "")}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* Public lead-magnet pages (driven by lib/publicPaths.js) */}
          {PUBLIC_PATHS
            .filter((p) => PUBLIC_ROUTE_COMPONENTS[p.path])
            .map((p) => (
              <Route key={p.path} path={p.path} element={PUBLIC_ROUTE_COMPONENTS[p.path]} />
            ))}

          {/* Short tracker link — used in WhatsApp / email outbound */}
          <Route path="/t/:token" element={<TrackShortLink />} />

          {/* CRM */}
          <Route path="/crm" element={
            <ProtectedRoute roles={["admin", "sales_employee"]}>
              <CrmLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Dashboard />} />
            <Route path="customers" element={<Customers />} />
            <Route path="materials" element={<Materials />} />
            <Route path="inventory" element={<Inventory />} />
            <Route path="vendors" element={<Vendors />} />
            <Route path="employees" element={<SalesEmployees />} />
            <Route path="technicians" element={<AdminTechnicians />} />
            <Route path="customer-groups" element={<CustomerGroups />} />
            <Route path="material-groups" element={<MaterialGroups />} />
            <Route path="leads" element={<Leads />} />
            <Route path="opportunities" element={<Opportunities />} />
            <Route path="opportunities/:id" element={<OpportunityDetail />} />
            <Route path="sales-targets" element={<SalesTargets />} />
            <Route path="quotations" element={<Quotations />} />
            <Route path="quotations/new" element={<Quotations />} />
            <Route path="quotations/:qid" element={<Quotations />} />
            <Route path="tickets" element={<ServiceTickets />} />
            <Route path="installed-base" element={<InstalledBase />} />
            <Route path="incentive-slabs" element={<IncentiveSlabs />} />
            <Route path="state-benefits" element={<StateBenefits />} />
            <Route path="solar-consultants" element={<SolarConsultants />} />
            <Route path="referrer-payouts" element={<ReferrerPayouts />} />
            <Route path="permissible-territories" element={<PermissibleTerritories />} />
            <Route path="service-team" element={<ServiceTeam />} />
            <Route path="sla" element={<SLAMaster />} />
            <Route path="campaigns" element={<Campaigns />} />
          </Route>

          {/* Service Team Portal — franchisees & managers */}
          <Route path="/service" element={
            <ProtectedRoute roles={["service_team", "admin"]}>
              <ServiceTeamLayout />
            </ProtectedRoute>
          }>
            <Route index element={<ServiceDashboard />} />
            <Route path="tickets" element={<ServiceTeamTickets />} />
            <Route path="technicians" element={<ServiceTeamTechnicians />} />
          </Route>

          {/* SC mobile-first portal */}
          <Route path="/sc/signup" element={<ScSignup />} />
          <Route path="/sc" element={
            <ProtectedRoute roles={["sc", "admin"]}>
              <ScLayout />
            </ProtectedRoute>
          }>
            <Route index element={<ScHome />} />
            <Route path="new-lead" element={<ScNewLead />} />
            <Route path="leads" element={<ScLeads />} />
            <Route path="service-requests" element={<ScServiceRequests />} />
            <Route path="earnings" element={<ScEarnings />} />
            <Route path="marketing" element={<ScMarketing />} />
            <Route path="marketing/creatives" element={<ScCampaigns />} />
            <Route path="marketing/flyer" element={<ScFlyer />} />
            <Route path="marketing/schemes" element={<ScSchemes />} />
            <Route path="marketing/calculator" element={<ScCalculator />} />
            <Route path="marketing/explainers" element={<ScExplainers />} />
            <Route path="marketing/faq" element={<ScFaq />} />
            <Route path="profile" element={<ScProfile />} />
            <Route path="target" element={<ScTarget />} />
            <Route path="schemes" element={<Navigate to="/sc/marketing/schemes" replace />} />
            <Route path="campaign" element={<Navigate to="/sc/marketing/flyer" replace />} />
          </Route>

          {/* Customer mobile app — phone-OTP installations, complaints & purchase */}
          <Route path="/app/login" element={<CustomerLogin />} />
          <Route path="/app/onboarding" element={
            <ProtectedRoute roles={["customer", "admin"]}>
              <CustomerOnboarding />
            </ProtectedRoute>
          } />
          <Route path="/app" element={
            <ProtectedRoute roles={["customer", "admin"]}>
              <CustomerAppLayout />
            </ProtectedRoute>
          }>
            <Route index element={<CustomerHome />} />
            <Route path="installations" element={<CustomerInstallations />} />
            <Route path="tickets" element={<CustomerTickets />} />
            <Route path="orders" element={<CustomerPastOrders />} />
            <Route path="raise-complaint" element={<CustomerRaiseComplaint />} />
            <Route path="buy-installation" element={<CustomerBuyInstallation />} />
            <Route path="profile" element={<CustomerProfile />} />
            <Route path="opportunities/:oid/quotation" element={<CustomerQuotation />} />
            <Route path="leads/:lid/quotation" element={<CustomerQuotation />} />
            <Route path="track/:oid" element={<CustomerTrackProgress />} />
            <Route path="referrals/:leadId" element={<CustomerReferralProgress />} />
          </Route>

          {/* FSM technician app */}
          <Route path="/fsm" element={
            <ProtectedRoute roles={["technician", "admin"]}>
              <FsmLayout />
            </ProtectedRoute>
          }>
            <Route index element={<FsmTickets />} />
            <Route path="warranty" element={<FsmWarranty />} />
            <Route path="lookup" element={<FsmLookup />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
